export type ImportCenterImporterKey =
  | 'production'
  | 'direct-inputs'
  | 'imported-manufacturing-orders'
  | 'purchase-orders';

export type ImportCenterDetectedType =
  | ImportCenterImporterKey
  | 'unknown'
  | 'excluded';

export type ImportCenterImporterDefinition = {
  key: ImportCenterImporterKey;
  label: string;
  description: string;
  acceptedExtensions: string[];
  filenameHints: string[];
  chipColor: 'success' | 'primary' | 'default' | 'warning';
  analysisPriority: number;
};

export type ImportCenterDetectionCandidate = {
  importerKey: ImportCenterImporterKey;
  score: number;
  matchedHints: string[];
};

export type ImportCenterDetectionResult = {
  detectedType: ImportCenterDetectedType;
  confidence: 'high' | 'medium' | 'low' | 'none';
  candidates: ImportCenterDetectionCandidate[];
  decisionReason: string;
};

export function getImportCenterOrderedCandidateKeys(
  detection: ImportCenterDetectionResult,
): ImportCenterImporterKey[] {
  if (detection.candidates.length > 0) {
    return detection.candidates.map((candidate) => candidate.importerKey);
  }

  return getImportCenterVisibleImporters().map((importer) => importer.key);
}

export const IMPORT_CENTER_ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'] as const;

export const IMPORT_CENTER_IMPORTERS: ImportCenterImporterDefinition[] = [
  {
    key: 'production',
    label: 'Producción',
    description: 'Importador de producción / partes de fabricación.',
    acceptedExtensions: [...IMPORT_CENTER_ACCEPTED_EXTENSIONS],
    filenameHints: [
      'produccion',
      'producción',
      'production',
      'fabricacion',
      'fabricación',
      'parte',
      'partes',
    ],
    chipColor: 'success',
    analysisPriority: 300,
  },
  {
    key: 'direct-inputs',
    label: 'Entradas',
    description: 'Importador de entradas / compras / materiales.',
    acceptedExtensions: [...IMPORT_CENTER_ACCEPTED_EXTENSIONS],
    filenameHints: [
      'entrada',
      'entradas',
      'compras',
      'purchase',
      'material',
      'materiales',
      'insumo',
      'insumos',
    ],
    chipColor: 'primary',
    analysisPriority: 200,
  },
  {
    key: 'imported-manufacturing-orders',
    label: 'Pedidos pendientes',
    description: 'Importador de pedidos pendientes de fabricar / OFs importadas.',
    acceptedExtensions: [...IMPORT_CENTER_ACCEPTED_EXTENSIONS],
    filenameHints: [
      'pedido pendiente',
      'pedidos pendientes',
      'pendiente fabricar',
      'pendientes fabricar',
      'manufacturing order',
      'manufacturing orders',
      'of pendiente',
      'ofs pendientes',
    ],
    chipColor: 'default',
    analysisPriority: 100,
  },
  {
    key: 'purchase-orders',
    label: 'Compras pendientes',
    description: 'Pedidos de compra pendientes de recibir (snapshot).',
    acceptedExtensions: [...IMPORT_CENTER_ACCEPTED_EXTENSIONS],
    filenameHints: [
      'pedidos de compra',
      'pedido de compra',
      'compra pendiente',
      'compras pendientes',
      'purchase order',
      'purchase orders',
    ],
    chipColor: 'warning',
    analysisPriority: 150,
  },
];

export function normalizeImportCenterText(value: string): string {
  return value.trim().toLowerCase();
}

export function isAcceptedImportCenterFile(name: string): boolean {
  const normalizedName = normalizeImportCenterText(name);
  return IMPORT_CENTER_ACCEPTED_EXTENSIONS.some((extension) =>
    normalizedName.endsWith(extension),
  );
}

export function evaluateImportCenterDetectionFromFilename(
  fileName: string,
): ImportCenterDetectionResult {
  const normalizedName = normalizeImportCenterText(fileName);

  const candidates = IMPORT_CENTER_IMPORTERS
    .map((importer) => {
      const matchedHints = importer.filenameHints.filter((hint) =>
        normalizedName.includes(hint),
      );

      return {
        importerKey: importer.key,
        score: matchedHints.length,
        matchedHints,
        analysisPriority: importer.analysisPriority,
      };
    })
    .filter((candidate) => candidate.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return b.analysisPriority - a.analysisPriority;
    })
    .map(({ importerKey, score, matchedHints }) => ({
      importerKey,
      score,
      matchedHints,
    }));

  if (candidates.length === 0) {
    return {
      detectedType: 'unknown',
      confidence: 'none',
      candidates: [],
      decisionReason: 'No se encontraron pistas suficientes en el nombre del archivo.',
    };
  }

  const bestCandidate = candidates[0];
  const secondCandidate = candidates[1];

  if (!secondCandidate) {
    return {
      detectedType: bestCandidate.importerKey,
      confidence: bestCandidate.score >= 2 ? 'high' : 'medium',
      candidates,
      decisionReason: `Asignación automática por coincidencia de nombre (${bestCandidate.matchedHints.join(', ')}).`,
    };
  }

  const scoreGap = bestCandidate.score - secondCandidate.score;

  if (scoreGap >= 1) {
    return {
      detectedType: bestCandidate.importerKey,
      confidence: bestCandidate.score >= 2 ? 'high' : 'medium',
      candidates,
      decisionReason: `Asignación automática por mejor coincidencia de nombre (${bestCandidate.matchedHints.join(', ')}).`,
    };
  }

  return {
    detectedType: 'unknown',
    confidence: 'low',
    candidates,
    decisionReason: 'Hay ambigüedad entre varios importadores candidatos.',
  };
}

export function detectImportCenterTypeFromFilename(
  fileName: string,
): ImportCenterDetectedType {
  return evaluateImportCenterDetectionFromFilename(fileName).detectedType;
}

export function getImportCenterImporterDefinition(
  key: ImportCenterImporterKey,
): ImportCenterImporterDefinition | null {
  return (
    IMPORT_CENTER_IMPORTERS.find((importer) => importer.key === key) ?? null
  );
}

export function getImportCenterVisibleImporters(): ImportCenterImporterDefinition[] {
  return [...IMPORT_CENTER_IMPORTERS].sort(
    (a, b) => b.analysisPriority - a.analysisPriority,
  );
}

export function getImportCenterSelectableDetectedTypes(): Array<{
  value: ImportCenterDetectedType;
  label: string;
}> {
  return [
    ...getImportCenterVisibleImporters().map((importer) => ({
      value: importer.key,
      label: importer.label,
    })),
    { value: 'unknown', label: 'Pendiente' },
    { value: 'excluded', label: 'Excluir del lote' },
  ];
}

export function getImportCenterDetectedTypeLabel(
  type: ImportCenterDetectedType,
): string {
  if (type === 'unknown') return 'Pendiente';
  if (type === 'excluded') return 'Excluido';

  return getImportCenterImporterDefinition(type)?.label ?? 'Pendiente';
}

export function getImportCenterDetectedTypeChipColor(
  type: ImportCenterDetectedType,
): 'success' | 'primary' | 'default' | 'warning' {
  if (type === 'unknown') return 'default';
  if (type === 'excluded') return 'warning';

  return getImportCenterImporterDefinition(type)?.chipColor ?? 'default';
}